"use client";

import { POSTHOG_API_HOST, stripSensitiveQueryParams } from "@/lib/analytics/constants";
import { dropInjectedScriptExceptions } from "@/lib/analytics/drop-injected-script-exceptions";
import { isProduction } from "@/utils/env-utils";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { initializeAgGridLicense } from "@/lib/ag-grid/license";

if (typeof window !== "undefined" && isProduction) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    api_host: POSTHOG_API_HOST,
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    // Manual pageview capture already strips SENSITIVE_QUERY_PARAMS (PostHogPageView.tsx), but
    // autocapture and session recording read $current_url/$referrer straight off the page on
    // every event — without this, the forms edit token (`t`, a non-expiring bearer credential)
    // leaks into PostHog regardless of the manual capture.
    sanitize_properties: (properties) => {
      if (typeof properties.$current_url === "string") {
        properties.$current_url = stripSensitiveQueryParams(properties.$current_url);
      }
      if (typeof properties.$referrer === "string") {
        properties.$referrer = stripSensitiveQueryParams(properties.$referrer);
      }
      return properties;
    },
    // Snapchat, Instagram and Brave on iOS inject scripts that throw against their own native
    // bridges; autocapture files those under our pages as if they were our bugs.
    before_send: dropInjectedScriptExceptions,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true, // Highly recommended as a minimum!!
        // color: false,
        // date: false,
        // 'datetime-local': false,
        // email: false,
        // month: false,
        // number: false,
        // range: false,
        // search: false,
        // tel: false,
        // text: false,
        // time: false,
        // url: false,
        // week: false,
        // textarea: false,
        // select: false,
      },
    },
  });
}

// Initialize AG Grid license
if (typeof window !== "undefined") {
  initializeAgGridLicense();
}

interface PHProviderProps extends React.PropsWithChildren {
  siteUrl: string;
}

/**
 * Wraps the app in the PostHog provider, but only in production — elsewhere the children
 * render untouched so local and preview runs emit no analytics.
 *
 * @param children - The subtree to render inside the provider.
 * @param siteUrl - Public site URL for the current deployment.
 * @returns The children, wrapped in `PostHogProvider` when running in production.
 */
export function PHProvider({ children, siteUrl }: PHProviderProps): JSX.Element {
  // Only use PostHog in production
  if (isProduction) {
    return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
  }
  return <>{children}</>;
}
